import React, { useState, useEffect, useRef } from "react";

const DealCard = ({ deal, navigate, idx }) => {
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHov] = useState(false);
  const [clicked, setClicked] = useState(false);

  const filterUrl = deal.condition === 'ABOVE'
    ? `/all-products?minPrice=${deal.price}`
    : `/all-products?minPrice=${deal.minPrice || 0}&maxPrice=${deal.price}`;

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    if (clicked) return;
    setClicked(true);
    setTimeout(() => navigate(filterUrl), 420);
  };

  const priceDisplay = `₹${Number(deal.price).toLocaleString('en-IN')}`;

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className="relative cursor-pointer overflow-hidden rounded-[24px] border border-blue-100 dark:border-blue-900 bg-white dark:bg-black"
      style={{
        boxShadow: hovered
          ? `0 20px 40px -10px rgba(59, 130, 246, 0.3)`
          : `0 8px 24px -10px rgba(59, 130, 246, 0.15)`,
        transform: visible
          ? `translateY(0) scale(${hovered ? 1.04 : clicked ? 1.07 : 1})`
          : 'translateY(48px)',
        opacity: visible ? 1 : 0,
        transition: visible
          ? `transform 0.55s cubic-bezier(0.23,1,0.32,1) ${idx * 0.1}s, box-shadow 0.35s ease, opacity 0.5s ease ${idx * 0.1}s`
          : 'none',
        minHeight: 280,
      }}
    >
      {/* Click flash */}
      {clicked && (
        <span className="absolute inset-0 z-50 rounded-[24px] bg-blue-500/20"
          style={{
            animation: 'click-flash 0.38s ease-out forwards',
            pointerEvents: 'none',
          }} />
      )}

      {/* Card body */}
      <div className="relative z-10 h-full p-4 flex flex-col items-center">
        
        {/* Floating product image */}
        {deal.imageUrl && (
          <div className="w-full aspect-[4/3] max-h-[200px] flex items-center justify-center mb-4"
            style={{
              animation: 'float-image 3.5s ease-in-out infinite',
              filter: `drop-shadow(0 16px 24px rgba(59, 130, 246, 0.2))`,
            }}>
            <img
              src={deal.imageUrl}
              alt={deal.title}
              className="w-full h-full object-contain block"
            />
          </div>
        )}

        {/* Price badge */}
        <div className="w-full rounded-xl p-2 text-center mb-4 transition-transform duration-300 ease-out border-2 border-dashed border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950"
          style={{ transform: hovered ? 'scale(1.04)' : 'scale(1)' }}>
          <p className="text-[9px] font-black tracking-widest text-blue-500 dark:text-blue-400 mb-1 uppercase">
            {deal.condition}
          </p>
          <p className="text-xl font-black leading-none tracking-tight text-black dark:text-white">
            {priceDisplay}
          </p>
        </div>

        {/* Title */}
        <p className="text-[11px] font-extrabold tracking-widest text-black dark:text-white text-center mb-4">
          {deal.title}
        </p>

        {/* Pulse CTA button */}
        <div className="relative flex items-center justify-center mt-auto">
          {hovered && (
            <span className="absolute -inset-1.5 rounded-full border-2 border-blue-400 dark:border-blue-600 pointer-events-none"
              style={{ animation: 'deal-ping 1.1s ease-out infinite' }} />
          )}
          <div className={`rounded-full px-4 py-1.5 flex items-center gap-2 transition-all duration-300
            ${hovered ? 'bg-blue-600 text-white shadow-[0_4px_18px_rgba(37,99,235,0.4)]' : 'bg-white dark:bg-black text-blue-600 border border-blue-200 dark:border-blue-800'}`}>
            <span className="text-[9px] font-bold tracking-[0.2em] uppercase">
              Shop Now
            </span>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none"
              style={{ transition: 'transform 0.3s', transform: hovered ? 'translateX(3px)' : 'translateX(0)' }}>
              <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DealCard;
